/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { usePlayer } from "@packages/media";
import type { ThemeType } from "@packages/ui";
import {
  Button,
  ModalDropdownMenuContext,
  ModalDropdownMenuHeader,
  ModalTabContent,
  numericPercentages,
  numericValues,
  radius,
  spacing,
  StopCountdown,
  useThemeValues,
} from "@packages/ui";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { Time } from "../../../primitives/Time.emotion";

const buttonStyle: Interpolation = {
  background: "none",
  border: "none",
  borderRadius: radius.full,
  height: "min-content",
  lineHeight: numericValues[0],
  marginBottom: spacing.m,
  padding: numericValues[0],
};
const containerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  rowGap: spacing.m,
  width: numericPercentages[100],
};
const timeStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.onOverlayMedHigh,
});

export type SleepTimerTabContentProps = {
  countdownSeconds: number;
  value: string;
};

export const SleepTimerTabContent = ({
  countdownSeconds,
  value,
}: SleepTimerTabContentProps) => {
  const t = useTranslations();
  const [time, setTime] = useState<number>(countdownSeconds);
  const timer = useRef<NodeJS.Timeout | null>(null);
  const player = usePlayer();
  const menu = useContext(ModalDropdownMenuContext);
  const theme = useThemeValues();

  const updateTime = useCallback(() => {
    if (player?.sleepTimerTarget && player.sleepTimerTarget > Date.now()) {
      const remaining = Math.ceil(
        (player.sleepTimerTarget - Date.now()) / 1000,
      );
      setTime(remaining);
    } else {
      setTime(0);
      player?.cancelSleepTimer();
    }
  }, [player]);

  useEffect(() => {
    updateTime();
    timer.current = setInterval(updateTime, 1000);

    return () => {
      clearInterval(timer.current ?? undefined);
    };
  }, [updateTime]);

  return (
    <ModalTabContent
      value={value}
      header={
        <ModalDropdownMenuHeader>
          {t("track_settings_timer")}
        </ModalDropdownMenuHeader>
      }
    >
      <div css={containerStyle}>
        <Time timeS={time} size={"xl"} css={timeStyle} />
        <Button
          onClick={() => {
            player?.cancelSleepTimer();
            menu.close();
          }}
          css={buttonStyle}
        >
          <StopCountdown
            total={countdownSeconds}
            remaining={time}
            stroke={theme.semanticColors.onOverlayMedHigh}
          />
        </Button>
      </div>
    </ModalTabContent>
  );
};
